import { useState, useMemo, useCallback } from "react";
import type { FormProduct } from "@shared/types";
import { useTranslation } from "react-i18next";
import { Input } from "@bcc/components/ui/input";
import { Alert, AlertDescription } from "@bcc/components/ui/alert";
import { Search, Loader2, AlertCircle } from "lucide-react";
import { getFormProductKey } from "@bcc/components/shopify-import/get-form-product-key";
import { ProductSelectFormRow } from "./product-select-form-row";
import { getProductSelectFormTestIds } from "./product-select-form.utils";

export interface ProductSelectFormProps {
  products: FormProduct[];
  selectedProducts: FormProduct[];
  onSelectionChange: (products: FormProduct[]) => void;
  onDeleteDraft?: (product: FormProduct) => Promise<void> | void;
  isLoading?: boolean;
  error?: string | null;
  isSingleBagSizeEnabled?: boolean;
  testId?: string;
  /** Controlled search value. When provided (with `onSearchQueryChange`), the
   *  input becomes controlled by the parent instead of owning its own state —
   *  used by WooCommerce remote search, which drives `products` itself. */
  searchQuery?: string;
  onSearchQueryChange?: (value: string) => void;
  /** `products` is already server-filtered (e.g. WooCommerce remote search) —
   *  skip the local title substring filter so a numeric ID query can't hide a
   *  real match. */
  skipLocalFilter?: boolean;
  /** Overrides the search input's placeholder/aria-label (e.g. a numeric-ID
   *  hint when searching WooCommerce products by ID). */
  searchPlaceholder?: string;
}

export function ProductSelectForm({
  products,
  selectedProducts,
  onSelectionChange,
  onDeleteDraft,
  isLoading = false,
  error = null,
  isSingleBagSizeEnabled = false,
  testId,
  searchQuery: controlledSearchQuery,
  onSearchQueryChange,
  skipLocalFilter = false,
  searchPlaceholder,
}: ProductSelectFormProps) {
  const { t } = useTranslation();
  const [internalSearchQuery, setInternalSearchQuery] = useState("");
  const searchQuery = controlledSearchQuery ?? internalSearchQuery;
  const setSearchQuery = onSearchQueryChange ?? setInternalSearchQuery;

  const { containerTestId, searchInputTestId, productListTestId, emptyStateTestId, loadingStateTestId, errorStateTestId } = useMemo(
    () => getProductSelectFormTestIds(testId),
    [testId]
  );

  const filteredProducts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const filtered = !skipLocalFilter && query ? products.filter((product) => product.title?.toLowerCase().includes(query)) : products;
    return [...filtered].sort((a, b) => (a.title ?? "").localeCompare(b.title ?? ""));
  }, [products, searchQuery, skipLocalFilter]);

  const isProductSelected = useCallback(
    (product: FormProduct) => {
      return selectedProducts.some((p) => getFormProductKey(p) === getFormProductKey(product));
    },
    [selectedProducts]
  );

  const handleProductSelect = useCallback(
    (product: FormProduct, checked: boolean) => {
      // Additive multi-select: toggle the product in/out of the selection by
      // its stable key instead of replacing the whole array (batch import).
      const key = getFormProductKey(product);
      const withoutProduct = selectedProducts.filter((p) => getFormProductKey(p) !== key);
      onSelectionChange(checked ? [...withoutProduct, product] : withoutProduct);
    },
    [onSelectionChange, selectedProducts]
  );

  if (isLoading) {
    return (
      <div data-testid={loadingStateTestId} className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert data-testid={errorStateTestId} variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div data-testid={containerTestId} className="space-y-4">
      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          data-testid={searchInputTestId}
          type="text"
          aria-label={searchPlaceholder ?? t("components.ProductSelectModal.searchPlaceholder")}
          placeholder={searchPlaceholder ?? t("components.ProductSelectModal.searchPlaceholder")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Product list */}
      {filteredProducts.length === 0 ? (
        <div data-testid={emptyStateTestId} className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-muted-foreground">
            {searchQuery
              ? t("components.ProductSelectModal.noProductsMatchSearch")
              : t("components.ProductSelectModal.noProductsAvailable")}
          </p>
        </div>
      ) : (
        <ul data-testid={productListTestId} className="space-y-2 max-h-[400px] overflow-y-auto">
          {filteredProducts.map((product) => (
            <ProductSelectFormRow
              key={getFormProductKey(product)}
              product={product}
              isSelected={isProductSelected(product)}
              onSelect={handleProductSelect}
              onDeleteDraft={onDeleteDraft}
              isSingleBagSizeEnabled={isSingleBagSizeEnabled}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
